import React, { useCallback } from 'react';
import { Platform, Text } from 'react-native';

import {
  FocusScope,
  TextSpace,
  useAppState,
  useKeyboardListener,
  useSafeFocusEffect,
  useSafeIsFocused,
  useSafeNavigation,
  useSafeRouter,
} from '@sg/garnish';
import { type DeliveryOrderDetailInput } from '@sg/graphql-schema';
import { type Product } from '@sg/graphql-schema';
import { BagRewardWarning } from '@sg/shared-fe/features/loyalty';

import { adjustWantedTime, Bag } from '@order/features/ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { isTomorrow } from '@order/utils';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { useNavigateToLastInteractedStore } from '../../../../context/LastInteractedStore';
import {
  useBagContent,
  useCloseBag,
  useIsUpsellScreenEnabled,
  useNavigateToMenu,
} from '../../../../hooks';
import { useBagDisclosure, type useOrderState, useUpsells } from '../../hooks';
import { BagLineItems } from '../BagLineItems';
import { BagLoyalty } from '../BagLoyalty';
import { CategoryUpsells } from '../CategoryUpsells';

export const BagContent = (props: BagContentProps) => {
  const { orderState } = props;

  // ─── Flags ────────────────────────────────────────────────────────────────

  const {
    isBagEmpty,
    isBagValid,
    isFetchingInitialData,
    isSubmittingOrder,
    isShowingFutureDateWarning,
    isShowingRewardWarning,
    isShowingTimeslotAdjustedNotice,
    isLineItemQuantityChangePending,
    isPerformingBagAction,
    isShowingWarning,
    isShowingModal,
    isPostOrder,
    isPostRedirectDelayed,
    isShowingOrderPlayback,
    isCancelingPreviousIntent,
  } = orderState;

  const isFocused = useSafeIsFocused();
  const isFocusable = !isShowingModal && !isShowingWarning;
  const canContinueToCheckout =
    !isPerformingBagAction && !isLineItemQuantityChangePending && !isShowingModal;
  const isUpsellScreenEnabled = useIsUpsellScreenEnabled();

  // ─── Context ──────────────────────────────────────────────────────────────

  const {
    bagTracking,
    wantedTime,
    previousWantedTime,
    dropoffLocation = '',
    lineItemIdsBeingUpdated,
    cart,
    rewardError,
    isRewardMissingSku,
    shouldIncludeUtensils,
    paymentStateStatus,
  } = orderState;

  // ─── Cart ─────────────────────────────────────────────────────────────────

  const {
    orderId = '',
    orderChannel = 'pickup',
    locationName = '',
    restaurantId = '',
    restaurantSlug = '',
    deliveryOrderDetail,
    dropoffLocations,
    addressId,
    lineItems,
    ledger,
    showDeliveryFeeDisclosure = false,
    showOutpostPriceDifferenciationDisclosure = false,
    showDeliveryPriceDifferenciationDisclosure = false,
    deliveryFee = 0,
    deliveryMinSubtotal = 0,
  } = cart;

  // ─── Actions ──────────────────────────────────────────────────────────────

  const {
    startPostRedirectDelay,
    addUpsell,
    startChangingLineItemQuantity,
    stopChangingLineItemQuantity,
    changeLineItemQuantity,
    cancelRequest,
    cancelPreviousIntent,
    requestChannelOrLocationChange,
    requestDeliveryPreferencesChange,
    requestTimeChange,
    requestOrderSubmission,
    changeDropoffLocation,
    changeUtensilsUsage,
    requestCheckoutLedger,
  } = orderState;

  // ─── Headers ──────────────────────────────────────────────────────────────

  const adjustedTime = adjustWantedTime(wantedTime);
  const isFutureDate = isTomorrow(adjustedTime);
  const hasLongerLocationName = locationName.length > 24;

  // ─── Tracking ─────────────────────────────────────────────────────────────

  const { trackViewMenuPressed, trackLineItemPressed } = bagTracking;

  // ─── Navigation ───────────────────────────────────────────────────────────

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const closeBag = useCloseBag();

  const handleViewMenu = useNavigateToLastInteractedStore({ isInsideModal: true }); // prettier-ignore
  const navigateToMenu = useNavigateToMenu();
  const navigateToBagMenu = useCallback(() => {
    trackViewMenuPressed();
    navigateToMenu({
      orderType: orderChannel,
      restaurantSlug,
      deliveryOrderDetail: { addressId },
      referrer: 'bag',
    });
  }, [orderChannel, restaurantSlug, addressId, navigateToMenu, trackViewMenuPressed]);

  // ─── Auth State ───────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();

  const handleContinue = useCallback(() => {
    if (!isLoggedIn) {
      if (isExpoRouterEnabled) {
        router?.dismissTo({
          pathname: '/(auth)/join-or-sign-in',
          params: { redirect: 'bag' },
        });
        return;
      }

      navigation?.navigate('Auth', {
        screen: 'Login',
        params: { redirect: 'bag' },
      });

      return;
    }

    requestCheckoutLedger();
  }, [isExpoRouterEnabled, isLoggedIn, navigation, requestCheckoutLedger, router]);

  // ─── Contentful ───────────────────────────────────────────────────────────

  const {
    viewMenuCta,
    viewMenuA11y,
    continueToCheckoutCta,
    deliveryDisclosure,
    outpostDisclosure,
    timeslotUnavailableWarning,
    timeslotUnavailableWarningContinueCta,
    timeslotUnavailableWarningBackCta,
    loadingTitle,
    postRedirectTitle,
    postRedirectBody,
    postRedirectDelayedTitle,
    postRedirectDelayedBody,
    postRedirectDelayedCta,
  } = useBagContent();

  const disclosure = useBagDisclosure({
    orderChannel,
    showDeliveryFeeDisclosure,
    showOutpostPriceDifferenciationDisclosure,
    showDeliveryPriceDifferenciationDisclosure,
    deliveryFee,
    deliveryMinSubtotal,
    deliveryDisclosure,
    outpostDisclosure,
  });

  // ─── Upsells ──────────────────────────────────────────────────────────────

  const { categoryUpsells, isLoadingUpsells, shouldRenderCategoryUpsells, hideCategoryUpsells } =
    useUpsells({ orderId, restaurantId, orderChannel, lineItems });

  const addUpsellToBag = useCallback(
    (payload: { product: Partial<Product>; deliveryDetails?: DeliveryOrderDetailInput }) => {
      const { product, deliveryDetails } = payload;

      addUpsell({
        productId: product.id ?? '',
        productName: product.name ?? '',
        deliveryDetails,
      });
    },
    [addUpsell],
  );

  // ─── Loading ───────────────────────────────────────────────────────────────

  const isPostRedirectSubmission = isSubmittingOrder && !isShowingOrderPlayback;
  const isLoadingBag =
    isFetchingInitialData || isLoadingUpsells || isPostOrder || isPostRedirectSubmission;

  // ─── Floating Ctas ────────────────────────────────────────────────────────
  // When the keyboard is open on Android, the floating cta hides the input field.

  const { isKeyboardOpen } = useKeyboardListener();
  const shouldHideFloatingCtas = Platform.OS === 'android' && isKeyboardOpen;
  const shouldShowFloatingCtas = !shouldHideFloatingCtas && isBagValid && !isLoadingBag;

  // ─── Loading State ────────────────────────────────────────────────────────

  const isPostRedirect = Boolean(paymentStateStatus);
  const isPollingEnabled = useFeatureFlag('CELS-4828-order-payment-state-polling-enabled'); // prettier-ignore
  const loadingTitlePostRedirect = isPostRedirectDelayed ? postRedirectDelayedTitle : postRedirectTitle; // prettier-ignore
  const loadingBodyPostRedirect = isPostRedirectDelayed ? postRedirectDelayedBody : postRedirectBody; // prettier-ignore
  const loadingStateTitleWhenEnabled = isPostRedirect ? loadingTitlePostRedirect : loadingTitle; // prettier-ignore
  const loadingStateTitle = isPollingEnabled ? loadingStateTitleWhenEnabled : ''; // prettier-ignore
  const loadingStateBody = isPostRedirect ? loadingBodyPostRedirect : ''; // prettier-ignore
  const loadingStateCta = isPostRedirect && isPostRedirectDelayed ? postRedirectDelayedCta : null; // prettier-ignore

  const handleStartPostRedirectDelay = useCallback(() => {
    if (!isPostRedirect || !isPollingEnabled) return;
    startPostRedirectDelay();
  }, [isPostRedirect, isPollingEnabled, startPostRedirectDelay]);

  useSafeFocusEffect(handleStartPostRedirectDelay);
  useAppState({ onVisible: handleStartPostRedirectDelay });

  // ─── Upsell Rendering ──────────────────────────────────────────────────────

  const canRenderUpsells = !isUpsellScreenEnabled && !isLoadingBag && !isPostRedirect; // prettier-ignore

  if (shouldRenderCategoryUpsells && canRenderUpsells) {
    return (
      <FocusScope enabled={Boolean(isFocused && isFocusable)}>
        <CategoryUpsells
          categories={categoryUpsells}
          deliveryOrderDetail={deliveryOrderDetail}
          upsellIdsBeingAdded={lineItemIdsBeingUpdated}
          addUpsellToBag={addUpsellToBag}
          onClose={closeBag}
          onContinue={hideCategoryUpsells}
        />
      </FocusScope>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <FocusScope enabled={Boolean(isFocused && isFocusable)}>
      {/* Loading state for the bag. */}
      {isLoadingBag ? (
        <Bag.LoadingState
          title={loadingStateTitle}
          body={loadingStateBody}
          cta={loadingStateCta}
          isLoading={isCancelingPreviousIntent}
          onPress={cancelPreviousIntent}
        />
      ) : null}

      {/* Empty state for the bag. */}
      {isBagEmpty && !isLoadingBag ? <Bag.EmptyState onViewMenu={handleViewMenu} /> : null}

      {/* Loading state overlay for when actions are performed in the bag, not needed for initial fetching or placing orders. */}
      <Bag.LoadingOpacity
        isLoading={Boolean(isPerformingBagAction && !isLoadingBag && !isSubmittingOrder)}
      />

      <Bag.WarningModal visible={isShowingWarning} onClose={cancelRequest}>
        {/* Warning that the selected date is in the future. */}
        {isShowingFutureDateWarning ? <Bag.FutureDateWarning onClose={cancelRequest} /> : null}

        {/* Warning that the reward was not applied. */}
        {isShowingRewardWarning ? (
          <BagRewardWarning
            message={rewardError}
            isRewardMissingSku={isRewardMissingSku}
            onViewMenu={handleViewMenu}
            onClose={cancelRequest}
          />
        ) : null}

        {/* Warning that the wanted time was adjusted. */}
        {isShowingTimeslotAdjustedNotice ? (
          <Bag.TimeslotUnavailableWarning
            wantedTime={wantedTime}
            previousWantedTime={previousWantedTime}
            message={timeslotUnavailableWarning}
            continueCta={timeslotUnavailableWarningContinueCta}
            backCta={timeslotUnavailableWarningBackCta}
            onContinue={requestOrderSubmission}
            onBack={requestTimeChange}
          />
        ) : null}
      </Bag.WarningModal>

      {/* Actual bag contents. */}
      {isBagValid && !isLoadingBag ? (
        <Bag.ScrollView
          orderChannel={orderChannel}
          locationName={locationName}
          viewMenuCta={viewMenuCta}
          viewMenuA11y={viewMenuA11y}
          isDisabled={isPerformingBagAction}
          automaticallyAdjustKeyboardInsets={!isShowingModal}
          onViewMenu={navigateToBagMenu}
          requestChannelOrLocationChange={requestChannelOrLocationChange}
          requestDeliveryPreferencesChange={requestDeliveryPreferencesChange}
          onHeaderCloseBtnPress={closeBag}
        >
          <Bag.ContentContainer>
            {/* Header with channel, location, time and dropoff location. */}
            <Bag.HeaderContainer>
              <Bag.HeaderLine sizeMatch={[hasLongerLocationName ? '24' : '32']} numberOfLines={3}>
                <Bag.ChannelHeader
                  orderChannel={orderChannel}
                  requestChannelOrLocationChange={requestChannelOrLocationChange}
                />

                <TextSpace />

                <Bag.TimeConnector time={wantedTime} />

                <TextSpace />

                <Bag.TimeHeader time={wantedTime} requestTimeChange={requestTimeChange} />

                <TextSpace />

                {isFutureDate ? null : <Text>{'\n'}</Text>}

                {orderChannel === 'pickup' ? <Bag.LocationFromConnector /> : null}

                {orderChannel === 'delivery' ? <Bag.LocationToConnector /> : null}

                {orderChannel === 'outpost' ? <Bag.LocationAtConnector /> : null}

                <TextSpace />

                <Bag.LocationHeader
                  orderChannel={orderChannel}
                  locationName={locationName}
                  requestChannelOrLocationChange={requestChannelOrLocationChange}
                  requestDeliveryPreferencesChange={requestDeliveryPreferencesChange}
                />
              </Bag.HeaderLine>
            </Bag.HeaderContainer>

            {orderChannel !== 'delivery' && dropoffLocations.length >= 2 ? (
              <Bag.DropoffLocationPicker
                selectedDropoffLocationId={dropoffLocation}
                firstDropoffLocation={dropoffLocations[0]}
                secondDropoffLocation={dropoffLocations[1]}
                changeDropoffLocation={changeDropoffLocation}
              />
            ) : null}

            {disclosure ? <Bag.Disclosure disclosure={disclosure} /> : null}

            {/* List of line items, able to edit quantity or change name. */}
            <BagLineItems
              lineItems={lineItems}
              restaurantSlug={restaurantSlug}
              addressId={addressId}
              isDelivery={orderChannel === 'delivery'}
              isDisabled={isPerformingBagAction}
              trackLineItemPressed={trackLineItemPressed}
              startChangingLineItemQuantity={startChangingLineItemQuantity}
              stopChangingLineItemQuantity={stopChangingLineItemQuantity}
              changeLineItemQuantity={changeLineItemQuantity}
            />

            {/* Rewards rail and add promo code field. */}
            <BagLoyalty orderState={orderState} />

            {orderChannel === 'delivery' ? (
              <Bag.UtensilsCta
                shouldIncludeUtensils={shouldIncludeUtensils}
                isDisabled={isPerformingBagAction}
                onChange={changeUtensilsUsage}
              />
            ) : null}

            <Bag.Footer isDisabled={isPerformingBagAction}>
              {ledger?.subtotal ? <Bag.Subtotal subtotal={ledger.subtotal} /> : null}

              {ledger?.discounts[0] ? <Bag.Discount {...ledger.discounts[0]} /> : null}
            </Bag.Footer>
          </Bag.ContentContainer>
        </Bag.ScrollView>
      ) : null}

      {shouldShowFloatingCtas ? (
        <Bag.FloatingCtas>
          <Bag.ContinueToCheckoutCta
            cta={continueToCheckoutCta}
            isDisabled={!canContinueToCheckout}
            onPress={handleContinue}
          />
        </Bag.FloatingCtas>
      ) : null}
    </FocusScope>
  );
};

// ─── Types ──────────────────────────────────────────────────────────────────

type BagContentProps = {
  orderState: ReturnType<typeof useOrderState>;
};
